import type { TExample } from '@atlas/uicorn/preview/modules';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import Dialog from '@atlas/uicorn/lib/Display/Dialog';
import type { TTooltipProps, TTooltipRef } from '@atlas/uicorn/lib/Display/Tooltip';
import Tooltip from '@atlas/uicorn/lib/Display/Tooltip';
import {
    useFloating,
    useDismiss,
    autoUpdate,
    useInteractions,
    flip,
    offset,
    shift,
    hide,
    type Placement,
} from '@floating-ui/react';
import useRefCallback from 'use-ref-callback';
import useOnChange from '@atlas/tool/box/hooks/useOnChange';
import useControlledState from '@atlas/tool/box/hooks/useControlledState';
import hotkey from '@atlas/tool/box/event/hotkey';

const mouseLeaveDelay = 1e2;

const Popover = React.memo(
    React.forwardRef(function Popover(
        props: TPopoverProps,
        ref: React.ForwardedRef<TPopoverRef>,
    ): React.ReactElement | null {
        const {
            className,
            children,
            content,
            tooltip,
            placement = 'bottom-start',
            strategy = 'absolute',
            zIndex = 100,
            open: propsOpen,
            defaultOpen,
            onOpenChange,
            onOpen,
            onClose,
            onKeyDown,
            trigger = 'click',
            raise,
            noFocusOnClose,
        } = props;

        const openedViaKeyboardRef = useRef(false);

        const [opened, setOpened] = useControlledState(
            propsOpen,
            (newOpen) => {
                onOpenChange?.(newOpen);
                if (newOpen) onOpen?.();
                else onClose?.();
            },
            defaultOpen,
        );

        const isOpen = useRefCallback(() => opened);
        const open = useRefCallback(() => setOpened(true));
        const close = useRefCallback(() => setOpened(false));
        const toggle = useRefCallback(() => setOpened((o) => !o));

        const tooltipRef = useRef<TTooltipRef>(null);
        const closeTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
        const isMouseOverTrigger = useRef(false);
        const isMouseOverContent = useRef(false);

        const [triggerElement, setTriggerElement] = useState<Element | null>(null);

        useEffect(
            function closeTooltipAfterOpened() {
                if (opened) tooltipRef.current?.close();
            },
            [opened],
        );

        useOnChange(
            function focusOnTriggerAfterClosed() {
                if (opened || noFocusOnClose || !triggerElement) return;
                if (!openedViaKeyboardRef.current) return;
                if (!('focus' in triggerElement) || typeof triggerElement.focus !== 'function') return;
                if (document.activeElement !== document.body) return;
                triggerElement.focus();
            },
            [opened],
        );

        const tooltipProps = useMemo<Omit<TTooltipProps, 'children'> | undefined>(() => {
            if (typeof tooltip === 'string') return { title: tooltip };
            if (typeof tooltip?.title !== 'undefined') return tooltip;
            return undefined;
        }, [tooltip]);

        const { refs, floatingStyles, context } = useFloating({
            open: opened,
            onOpenChange: setOpened,
            placement,
            strategy,
            middleware,
            whileElementsMounted: autoUpdate,
        });
        const dismiss = useDismiss(context);
        const interactions = useMemo(() => [dismiss], [dismiss]);
        const { getReferenceProps, getFloatingProps } = useInteractions(interactions);

        const getDialog = useRefCallback(() => refs.floating.current as HTMLDialogElement);

        useImperativeHandle(ref, () => ({ isOpen, open, close, toggle, getDialog }));

        const setTriggerRef = useRefCallback((reference: Element | null) => {
            if (reference !== null && !(reference instanceof Element)) {
                reference = null;
                console.error('Popover: Children ref must be an Element');
            }

            setTriggerElement(reference);

            refs.setReference(reference);
            if (children && 'ref' in children) {
                if (typeof children.ref === 'function') {
                    children.ref(reference);
                } else if (typeof children.ref === 'object' && children.ref !== null && 'current' in children.ref) {
                    children.ref.current = reference;
                }
            }
        });

        if (!children) return children ?? null;

        const closeHoverContent = (args: unknown[]) => {
            closeTimeoutId.current && clearTimeout(closeTimeoutId.current);

            closeTimeoutId.current = setTimeout(() => {
                if (isMouseOverTrigger.current || isMouseOverContent.current || isPrevented(args[0])) {
                    return;
                }
                close();
            }, mouseLeaveDelay);
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLDialogElement>) => {
            onKeyDown?.(event);

            if (event.defaultPrevented) return;

            if (escapeHotkey.matches(event)) {
                event.preventDefault();
                close();
            }
        };

        const popoverContent =
            opened &&
            triggerElement &&
            typeof content !== 'undefined' &&
            content !== null &&
            ReactDOM.createPortal(
                <SPopover
                    {...getFloatingProps({
                        children: typeof content === 'function' ? content({ opened, open, close, toggle }) : content,
                        style: floatingStyles,
                        className,
                    })}
                    {...(trigger.includes('persistentHover')
                        ? {
                              onMouseEnter: () => (isMouseOverContent.current = true),
                              onMouseLeave: (...args: unknown[]) => {
                                  isMouseOverContent.current = false;
                                  closeHoverContent(args);
                              },
                          }
                        : {})}
                    elevation={raise ? 'medium' : 'low'}
                    onKeyDown={handleKeyDown}
                    $zIndex={zIndex}
                    // Use ref to onOpen/onClose/onOpenChange
                    ref={refs.setFloating}
                />,
                triggerElement.closest('dialog') ?? document.body,
            );

        const child =
            typeof children === 'function'
                ? children({ ref: setTriggerRef, opened, toggle, open, close })
                : {
                      ...React.cloneElement(
                          children,
                          getReferenceProps({
                              ...children.props,
                              ...(trigger.includes('click')
                                  ? {
                                        onClick: (...args: unknown[]) => {
                                            children.props.onClick?.(...args);
                                            if (isPrevented(args[0])) return;

                                            const event = args[0] as MouseEvent;
                                            openedViaKeyboardRef.current = event.detail === 0;

                                            prevent(args[0]);
                                            toggle();
                                        },
                                    }
                                  : {}),
                              ...(trigger.includes('contextMenu')
                                  ? {
                                        onContextMenu: (...args: unknown[]) => {
                                            children.props.onContextMenu?.(...args);
                                            if (isPrevented(args[0])) return;

                                            openedViaKeyboardRef.current = false;
                                            prevent(args[0]);
                                            open();
                                        },
                                        onClick: (...args: unknown[]) => {
                                            close();
                                            children.props.onClick?.(...args);
                                        },
                                    }
                                  : {}),
                              ...(trigger.includes('hover')
                                  ? {
                                        onMouseEnter: (...args: unknown[]) => {
                                            children.props.onMouseEnter?.(...args);
                                            if (isPrevented(args[0])) return;

                                            openedViaKeyboardRef.current = false;
                                            open();
                                        },
                                        onMouseLeave: (...args: unknown[]) => {
                                            children.props.onMouseLeave?.(...args);
                                            if (isPrevented(args[0])) return;

                                            close();
                                        },
                                    }
                                  : {}),
                              ...(trigger.includes('persistentHover')
                                  ? {
                                        onMouseEnter: (...args: unknown[]) => {
                                            children.props.onMouseEnter?.(...args);
                                            isMouseOverTrigger.current = true;

                                            if (isPrevented(args[0])) return;

                                            openedViaKeyboardRef.current = false;
                                            open();
                                        },
                                        onMouseLeave: (...args: unknown[]) => {
                                            isMouseOverTrigger.current = false;
                                            children.props.onMouseLeave?.(...args);
                                            closeHoverContent(args);
                                        },
                                    }
                                  : {}),
                          }),
                      ),
                      ref: setTriggerRef,
                  };

        return (
            <>
                {tooltipProps ? (
                    <Tooltip
                        {...tooltipProps}
                        open={popoverContent ? false : tooltipProps.open}
                        children={child}
                        ref={tooltipRef}
                    />
                ) : (
                    child
                )}
                {popoverContent}
            </>
        );
    }),
);

export default Popover;

// #region Helpers

function isPrevented(argument: unknown) {
    return (
        typeof argument === 'object' && argument !== null && 'defaultPrevented' in argument && argument.defaultPrevented
    );
}

function prevent(argument: unknown) {
    if (
        typeof argument === 'object' &&
        argument !== null &&
        'preventDefault' in argument &&
        typeof argument.preventDefault === 'function'
    ) {
        argument.preventDefault();
    }
}

const middleware = [
    offset(4),
    // TODO: Doesn't work this way (https://floating-ui.com/docs/hide)
    hide({ strategy: 'referenceHidden' }),
    // TODO: Doesn't work for horizontal scroll (https://floating-ui.com/docs/flip)
    flip({ mainAxis: true, crossAxis: true, flipAlignment: true }),
    shift({ padding: 4 }),
];

const escapeHotkey = hotkey('escape', 'hidden');

// #endregion

// #region Styles

const appearKeyframes = keyframes`
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
`;

const SPopover = styled(Dialog).attrs({ open: true })<{ $zIndex: number }>`
    background-color: ${(p) => p.theme.color.grey02};
    z-index: ${(p) => p.$zIndex};
    animation: ${appearKeyframes} 0.2s ease forwards;
`;

// #endregion

// #region Types

export type TPopoverRef = {
    isOpen(): boolean;
    open(): void;
    close(): void;
    toggle(): void;
    getDialog(): HTMLDialogElement | null;
};

export type TPopoverPlacement = Placement;

type TPopoverContentProps = {
    opened: boolean;
    open(): void;
    close(): void;
    toggle(): void;
};

type TPopoverChildrenProps = {
    ref: (element: Element | null) => void;
    opened: boolean;
    open(): void;
    close(): void;
    toggle(): void;
};

export type TPopoverProps = {
    className?: string;
    children?: React.ReactElement | null | ((props: TPopoverChildrenProps) => React.ReactElement | null);
    content?: React.ReactElement | null | ((props: TPopoverContentProps) => React.ReactElement | null);
    placement?: TPopoverPlacement;
    strategy?: 'absolute' | 'fixed';
    tooltip?: string | TTooltipProps | null;
    open?: boolean;
    defaultOpen?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    onOpenChange?: (open: boolean) => void;
    onKeyDown?: React.KeyboardEventHandler<HTMLDialogElement>;
    trigger?: TPopoverTrigger[] | TPopoverTrigger;
    raise?: boolean;
    zIndex?: number;
    noFocusOnClose?: boolean;
};

export type TPopoverTrigger = 'click' | 'contextMenu' | 'hover' | 'persistentHover';

// #endregion

// #region Examples

export const Example: TExample = {
    position: 'page',
    containerStyle: {
        padding: 250,
        height: '200%',
        width: '200%',
        alignItems: 'flex-start',
    },
    render(props) {
        return (
            <Popover
                placement={props.placement}
                open={props.open}
                content={<img src="https://loremflickr.com/200/200" width={200} height={200} />}
                tooltip={props.withTooltip ? 'Tooltip text' : undefined}
                trigger={props.trigger}
                onOpenChange={props.onOpenChange}
                onOpen={props.onOpen}
                onClose={props.onClose}
            >
                <button onClick={props.onTriggerClick}>{props.trigger} to trigger</button>
            </Popover>
        );
    },
    controls: {
        placement: [
            'top',
            'right',
            'bottom',
            'left',
            'left-end',
            'left-start',
            'right-end',
            'right-start',
            'bottom-end',
            'bottom-start',
            'top-end',
            'top-start',
        ],
        withTooltip: 'boolean',
        open: { type: 'boolean', initial: true, connected: false },
        trigger: ['click', 'contextMenu', 'hover', 'persistentHover'],
    },
    handlers: {
        onTriggerClick: 'console',
        onOpenChange: 'console',
        onOpen: 'console',
        onClose: 'console',
    },
    figma: [
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=21-1284&mode=design&t=dl6EllQ4tm9YKdhg-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=52-5474&mode=design&t=dl6EllQ4tm9YKdhg-0',
    ],
};

export function FunctionalExample() {
    return (
        <Popover content={<img src="https://loremflickr.com/200/200" width={200} height={200} />}>
            {({ ref, opened, open, close, toggle }) => (
                <div ref={ref}>
                    <p>props.opened == {opened ? 'true' : 'false'}</p>
                    <button children="props.open()" onClick={open} />
                    <button children="props.close()" onClick={close} />
                    <button children="props.toggle()" onClick={toggle} />
                </div>
            )}
        </Popover>
    );
}

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Popover', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
